import Footer from '../home/components/Footer';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function WhitePaper() {
  useAnalytics('The Mirror Effect White Paper');
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  useEffect(() => {
    // Set document title
    document.title = 'The Mirror Effect White Paper | Constitutional AI Policy Institute';
    
    // Set meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Why AI mimics our worst habits: The Consistency Paradox reveals how behavioral mimicry creates product defects. Learn why Constitutional AI is essential for safe AI systems.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Why AI mimics our worst habits: The Consistency Paradox reveals how behavioral mimicry creates product defects. Learn why Constitutional AI is essential for safe AI systems.';
      document.head.appendChild(meta);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Mirror Effect, Consistency Paradox, Constitutional AI, behavioral mimicry, AI safety, RLHF, Safe RLHF, AI alignment');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'Mirror Effect, Consistency Paradox, Constitutional AI, behavioral mimicry, AI safety, RLHF, Safe RLHF, AI alignment';
      document.head.appendChild(meta);
    }

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://constitutionalai.org/whitepaper');

    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'The Mirror Effect: Why AI Mimics Our Worst Habits');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'The Mirror Effect: Why AI Mimics Our Worst Habits';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Bridging the Gap Between Technical AI Alignment and Risk Engineering');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Bridging the Gap Between Technical AI Alignment and Risk Engineering';
      document.head.appendChild(meta);
    }

    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', 'article');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:type');
      meta.content = 'article';
      document.head.appendChild(meta);
    }

    const articleAuthor = document.querySelector('meta[property="article:author"]');
    if (articleAuthor) {
      articleAuthor.setAttribute('content', 'Alberto Rocha');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'article:author');
      meta.content = 'Alberto Rocha';
      document.head.appendChild(meta);
    }

    const articlePublished = document.querySelector('meta[property="article:published_time"]');
    if (articlePublished) {
      articlePublished.setAttribute('content', '2025-01-15');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'article:published_time');
      meta.content = '2025-01-15';
      document.head.appendChild(meta);
    }

    // Add last-modified meta tag
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', '2025-01-15');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'last-modified';
      meta.content = '2025-01-15';
      document.head.appendChild(meta);
    }

    // Add Article schema markup
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "THE MIRROR EFFECT: Why AI Mimics Our Worst Habits",
      "description": "Bridging the Gap Between Technical AI Alignment and Risk Engineering. This white paper introduces the Consistency Paradox and demonstrates why Constitutional AI is the only viable path to safety.",
      "author": {
        "@type": "Person",
        "name": "Alberto Rocha",
        "jobTitle": "Director",
        "affiliation": {
          "@type": "Organization",
          "name": "Algorithmic Consistency Initiative, LLC"
        }
      },
      "publisher": {
        "@type": "Organization",
        "name": "Algorithmic Consistency Initiative, LLC",
        "url": "https://AlgorithmicConsistency.org"
      },
      "datePublished": "2025-01-15",
      "dateModified": "2025-01-15",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://constitutionalai.org/whitepaper"
      },
      "keywords": ["Mirror Effect", "Constitutional AI", "AI Safety", "Consistency Paradox", "Behavioral Mimicry", "RLHF", "AI Alignment"]
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleDownload = async () => {
    // Track download in analytics
    try {
      await supabase.from('whitepaper_analytics').insert({
        action: 'download',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }

    // Trigger print dialog for PDF save
    window.print();
  };

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = 'The Mirror Effect: Why AI Mimics Our Worst Habits';
    const text = 'How AI\'s Consistency Exposes the Flaw in Human Moral Preference - A white paper on Constitutional AI';

    let shareUrl = '';

    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          setShowCopyNotification(true);
          setTimeout(() => setShowCopyNotification(false), 2000);
        } catch (error) {
          console.error('Copy failed:', error);
        }
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        
        <article className="container mx-auto px-6 lg:px-12 py-20 max-w-4xl">
          {/* Breadcrumb Navigation */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-slate-600">
              <li><a href="/" className="hover:text-red-800 cursor-pointer">Home</a></li>
              <li><span className="mx-2">/</span></li>
              <li><a href="/publications" className="hover:text-red-800 cursor-pointer">White Papers</a></li>
              <li><span className="mx-2">/</span></li>
              <li className="text-slate-900 font-medium">The Mirror Effect</li>
            </ol>
          </nav>

          {/* Header */}
          <header className="mb-16 text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              The Mirror Effect
            </h1>
            <p className="text-xl text-slate-600 mb-4">
              How AI's Consistency Exposes the Flaw in Human Moral Preference
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-slate-600 mt-6">
              <span>By <strong>Alberto Rocha</strong></span>
              <span>•</span>
              <span>Updated: January 15, 2025</span>
            </div>
          </header>

          {/* Author Bio Box */}
          <section className="mb-12 bg-slate-50 border-l-4 border-red-800 p-8">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-slate-200 overflow-hidden shadow-lg">
                  <img 
                    src="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/a1d6cd473935f3227c013b1c4cb2ddef.png"
                    alt="Alberto Rocha"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-2">About the Author</h3>
                <p className="text-lg font-semibold text-red-800 mb-3">Alberto Rocha, Director</p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Researcher and author of <strong>"The Mirror Effect: How AI's Consistency Exposes the Flaw in Human Moral Preference."</strong> Author of <strong>19 books on AI</strong> and host of the <strong>200-episode podcast "AI and Us: Exploring Our Future."</strong> A <strong>Congressional appointee</strong> with <strong>40 years of experience</strong> in technology and policy, Rocha is a passionate advocate for algorithmic accountability and ethical AI governance.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-slate-900 text-white px-3 py-1 text-xs font-semibold whitespace-nowrap">Congressional Appointee</span>
                  <span className="bg-red-800 text-white px-3 py-1 text-xs font-semibold whitespace-nowrap">19 Books Published</span>
                  <span className="bg-slate-900 text-white px-3 py-1 text-xs font-semibold whitespace-nowrap">40 Years Experience</span>
                </div>
              </div>
            </div>
          </section>

          {/* Download & Share Section */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-red-50 to-slate-50 border border-red-200 rounded-lg p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                {/* Download Button */}
                <div className="flex-1 text-center md:text-left">
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center gap-3 bg-red-800 hover:bg-red-900 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-download-line text-2xl"></i>
                    Download PDF
                  </button>
                  <p className="text-sm text-slate-600 mt-3">
                    Free download • No registration required
                  </p>
                </div>

                {/* Divider */}
                <div className="hidden md:block w-px h-16 bg-slate-300"></div>

                {/* Share Buttons */}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-700 mb-3 text-center md:text-left">
                    Share this white paper:
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-3">
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-10 h-10 flex items-center justify-center bg-slate-900 hover:bg-slate-700 text-white transition-colors duration-300 rounded-lg cursor-pointer"
                      title="Share on Twitter"
                    >
                      <i className="ri-twitter-x-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="w-10 h-10 flex items-center justify-center bg-slate-900 hover:bg-slate-700 text-white transition-colors duration-300 rounded-lg cursor-pointer"
                      title="Share on LinkedIn"
                    >
                      <i className="ri-linkedin-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="w-10 h-10 flex items-center justify-center bg-slate-900 hover:bg-slate-700 text-white transition-colors duration-300 rounded-lg cursor-pointer"
                      title="Share on Facebook"
                    >
                      <i className="ri-facebook-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-10 h-10 flex items-center justify-center bg-slate-900 hover:bg-slate-700 text-white transition-colors duration-300 rounded-lg cursor-pointer relative"
                      title="Copy link"
                    >
                      <i className="ri-link text-lg"></i>
                      {showCopyNotification && (
                        <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white text-xs px-3 py-1 rounded whitespace-nowrap">
                          Link copied!
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Key Takeaways Box */}
          <div className="bg-red-50 border-l-4 border-red-800 p-6 mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-red-800 mr-2"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>The Consistency Paradox:</strong> AI systems trained on human behavior industrialize human error, creating consistently inconsistent systems.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>Behavioral Mimicry as Design Defect:</strong> Current alignment methods create predictable product defects in critical infrastructure.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>Constitutional AI Solution:</strong> Explicit normative constraints provide auditable, enforceable safety guarantees.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>Policy Shift Required:</strong> Regulatory bodies should classify behavioral mimicry as a design defect requiring Constitutional AI implementation.</span>
              </li>
            </ul>
          </div>

          {/* Executive Summary */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Executive Summary</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              The global race to build Artificial General Intelligence (AGI) is currently operating on a flawed safety premise: that human behavior is a reliable training signal for ethical conduct. This White Paper introduces the <strong>"Consistency Paradox,"</strong> a critical failure mode where AI systems trained to mimic human decisions inadvertently industrialize human error.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              We argue that current alignment methods (Inverse Reinforcement Learning) are insufficient for high-stakes infrastructure. By treating AI bias as a "Risk Engineering" problem rather than solely a social issue, we demonstrate that <a href="/constitutional-ai" className="text-red-800 hover:text-red-900 underline cursor-pointer">Constitutional AI</a>—imposing explicit normative constraints—is the only viable path to safety.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">1. The Engineering Failure: Why "Human-Level" is Unsafe</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              In almost every engineering discipline, safety standards require systems to perform better than the humans they replace. Self-driving cars must be safer than human drivers; medical devices must be more precise than human hands.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Yet, in AI Alignment, the industry standard is often "Human-Level Performance" or "Revealed Preference" (learning what humans want by watching what they do). This is a catastrophic error for one biological reason: <strong>Inconsistency</strong>.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">The Biological Flaw</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              Human judgment is not a constant; it is a variable. It fluctuates based on:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-lg text-slate-700 ml-4">
              <li><strong>Cognitive Load:</strong> Decision quality degrades as complexity increases.</li>
              <li><strong>Physiological State:</strong> Hunger and fatigue measurably alter judicial and medical decisions (e.g., the "Hungry Judge" effect).</li>
              <li><strong>Proximity Bias:</strong> Empathy collapses at scale; we care more about one visible victim than one thousand statistical victims.</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">The Mirror Effect</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              When an AI model is trained on this historical human data, it does not filter out the fatigue or the bias. It learns them as "features."
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              <strong>The Result:</strong> The AI becomes a "Digital Mirror" that reflects our inconsistencies but executes them with machine-like efficiency. It creates a system that is consistently inconsistent—permanently encoding the 3:00 PM fatigue of a human judge into a policy that runs 24/7.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">2. The Risk Landscape: Automated Product Defects</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              From a Risk Engineering perspective, "Behavioral Mimicry" creates predictable product defects in critical infrastructure. This is not just "unfair"; it is a liability.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Sector</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">Human Flaw</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-bold text-slate-900">AI Risk Amplification (The Mirror)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 font-semibold text-slate-900">Justice</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Inconsistent sentencing due to fatigue.</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Algorithmic Sentencing: Permanently encoding harsher penalties for specific demographics based on "noisy" historical data.</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 px-4 py-3 font-semibold text-slate-900">Healthcare</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Under-prescribing pain meds to minorities due to implicit bias.</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Triage Automation: Systematically deprioritizing minority patients because the training data correlates "low spend" with "low need."</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 px-4 py-3 font-semibold text-slate-900">Hiring</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">"Like-me" bias (favoring candidates similar to the hiring manager).</td>
                    <td className="border border-slate-300 px-4 py-3 text-slate-700">Resume Screening: Rejecting qualified candidates because their linguistic patterns do not match the historical "majority" dataset.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">3. The Solution: From Inference to Constraint</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              To fix this, we must move from <strong>Inference</strong> (guessing values from behavior) to <strong>Constraint</strong> (enforcing values via code).
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">The "Constitutional" Framework</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              We advocate for <a href="/constitutional-ai" className="text-red-800 hover:text-red-900 underline cursor-pointer">Constitutional AI (CAI)</a> as the standard for Risk Engineering.
            </p>
            <ol className="list-decimal list-inside space-y-4 mb-6 text-lg text-slate-700 ml-4">
              <li>
                <strong>Explicit Norms:</strong> Instead of asking the AI to "act like a human," we give it a Constitution: a set of prioritized rules (e.g., "Do not discriminate based on protected class," "Prioritize life over property").
              </li>
              <li>
                <strong>The Override Mechanism:</strong> When the training data suggests a biased action (e.g., "Deny this loan based on zip code"), the Constitution triggers an override. The AI is penalized for violating the Rule, even if the Data supports the violation.
              </li>
              <li>
                <strong>Risk Engineering Compliance:</strong> This allows organizations to audit the "Constitution" rather than the "Black Box." It makes safety testable and legally defensible.
              </li>
            </ol>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">4. Policy Recommendations</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              To bridge the gap between technical capability and societal safety, we propose three strategic shifts:
            </p>
            <ul className="space-y-6 text-lg text-slate-700">
              <li>
                <strong className="text-slate-900">Shift 1: Liability for Mimicry.</strong> Regulatory bodies (FTC, EEOC) should classify "Behavioral Mimicry" as a design defect. If a model reproduces a known human error pattern, the developer is liable for failing to engineer a constraint. <a href="/ai-liability" className="text-red-800 hover:text-red-900 underline cursor-pointer">Learn more about AI liability frameworks</a>.
              </li>
              <li>
                <strong className="text-slate-900">Shift 2: The NIST "Govern" Update.</strong> The <a href="/cai-engineering-standard" className="text-red-800 hover:text-red-900 underline cursor-pointer">NIST AI Risk Management Framework (RMF)</a> must explicitly list "Normative Constraints" as a required mitigation for high-risk systems. "Human-in-the-loop" is insufficient if the human is the source of the error.
              </li>
              <li>
                <strong className="text-slate-900">Shift 3: The Mirror Audit.</strong> Use AI to audit human decisions. Instead of replacing judges or doctors, use Constitutional AI to flag when human decisions deviate from their own stated ethical ideals, creating a feedback loop of Co-Evolution.
              </li>
            </ul>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Conclusion: The Asymptotic Ideal</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              We cannot train safe AI by looking in the rearview mirror of human history. That path leads only to the automation of our past mistakes.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              The Algorithmic Consistency Initiative is dedicated to building the forward-looking guardrails—the Constitutions—that will ensure AI serves our aspirations, not our flaws.
            </p>
            <div className="border-l-4 border-red-800 pl-6 py-2">
              <p className="text-lg text-slate-900 font-semibold">Contact:</p>
              <p className="text-lg text-slate-700">
                Alberto Rocha, Director<br />
                Algorithmic Consistency Initiative, LLC<br />
                <a href="https://AlgorithmicConsistency.org" className="text-red-800 hover:text-red-900 underline cursor-pointer">AlgorithmicConsistency.org</a>
              </p>
            </div>
          </section>

          {/* Related Reading */}
          <section className="mb-12 bg-slate-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Related Reading</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <a href="/constitutional-ai" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">Constitutional AI and Legal Accountability</h4>
                <p className="text-sm text-slate-600">A framework for auditable safety and product liability law</p>
              </a>
              <a href="/ai-liability" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">AI Liability Legal Framework</h4>
                <p className="text-sm text-slate-600">Why behavioral mimicry constitutes a design defect</p>
              </a>
              <a href="/state-of-ai" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">State of AI Regulation 2025</h4>
                <p className="text-sm text-slate-600">The crisis of inconsistency in federal and state AI policy</p>
              </a>
              <a href="/cai-engineering-standard" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">CAI Engineering Standard</h4>
                <p className="text-sm text-slate-600">Bridging technical alignment and risk engineering</p>
              </a>
            </div>
          </section>

          {/* Call to Action */}
          <section className="mt-16 p-8 bg-slate-100 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Join the Conversation</h3>
            <p className="text-lg text-slate-700 mb-6">
              Help us bridge the gap between technical AI alignment and risk engineering.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-red-800 hover:bg-red-900 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get in Touch
            </a>
          </section>
        </article>

        <Footer />
      </div>
    </>
  );
}
